/**
 * Mobile Welcome Screen - Refined
 *
 * Clean, professional onboarding slider for mobile users.
 *
 * Improvements:
 * - Larger circular icons as visual anchors
 * - Selling points moved INTO carousel slides (removed bottom clutter)
 * - Animated pagination dots
 * - Better vertical rhythm and spacing
 * - Login button with icon
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import type { PanInfo } from 'framer-motion';
import {
  Sparkles,
  ArrowRight,
  Play,
  Link2,
  ShieldCheck,
  LogIn,
} from 'lucide-react';
import { ContentSageIcon } from '../ContentSageLogo';

interface Slide {
  id: number;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  iconBg: string;
  iconBorder: string;
}

const slides: Slide[] = [
  {
    id: 1,
    title: 'Your SEO Oracle',
    subtitle: 'The only AI writer that automates internal linking & verified citations.',
    icon: <Sparkles className="w-16 h-16 text-purple-400" />,
    iconBg: 'bg-purple-500/20',
    iconBorder: 'border-purple-500/30',
  },
  {
    id: 2,
    title: "Weaver's Web",
    subtitle: 'Stop orphaned pages. We auto-inject contextual internal links to boost authority.',
    icon: <Link2 className="w-16 h-16 text-purple-400" />,
    iconBg: 'bg-purple-500/20',
    iconBorder: 'border-purple-500/30',
  },
  {
    id: 3,
    title: 'Truth Spell',
    subtitle: 'Zero hallucinations. Every claim verified against 200M+ academic papers.',
    icon: <ShieldCheck className="w-16 h-16 text-emerald-400" />,
    iconBg: 'bg-emerald-500/20',
    iconBorder: 'border-emerald-500/30',
  },
];

export const MobileWelcomeScreen: React.FC = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  const handleDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;
    if (info.offset.x > threshold && activeSlide > 0) {
      setDirection(-1);
      setActiveSlide(activeSlide - 1);
    } else if (info.offset.x < -threshold && activeSlide < slides.length - 1) {
      setDirection(1);
      setActiveSlide(activeSlide + 1);
    }
  };

  const goToSlide = (index: number) => {
    setDirection(index > activeSlide ? 1 : -1);
    setActiveSlide(index);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col pt-[env(safe-area-inset-top)]">
      {/* Navbar */}
      <header className="px-6 py-4 flex items-center justify-between z-10">
        <div className="flex items-center gap-2">
          <ContentSageIcon size={32} />
          <span className="text-white font-bold text-xl tracking-wide">
            ContentSage
          </span>
        </div>
        <Link
          to="/login"
          className="flex items-center gap-1.5 px-4 py-2 bg-slate-800/50 rounded-full hover:bg-slate-800 transition"
        >
          <span className="text-slate-300 font-medium text-sm">Log In</span>
          <LogIn className="w-4 h-4 text-slate-400" />
        </Link>
      </header>

      {/* Carousel Container */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={activeSlide}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.2}
            onDragEnd={handleDragEnd}
            className="flex flex-col items-center text-center cursor-grab active:cursor-grabbing px-4"
          >
            {/* Large Circular Icon Container */}
            <div
              className={`w-32 h-32 rounded-full ${slides[activeSlide].iconBg} ${slides[activeSlide].iconBorder} border flex items-center justify-center mb-8 shadow-xl`}
            >
              {slides[activeSlide].icon}
            </div>

            {/* Title */}
            <h1 className="text-white text-4xl font-bold text-center mb-4 leading-tight">
              {slides[activeSlide].title}
            </h1>

            {/* Subtitle */}
            <p className="text-slate-400 text-lg text-center leading-relaxed max-w-sm px-2">
              {slides[activeSlide].subtitle}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Animated Pagination Dots */}
        <div className="flex gap-3 mt-10">
          {slides.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => goToSlide(index)}
              className="h-2 rounded-full"
              initial={false}
              animate={{
                width: index === activeSlide ? 24 : 8,
                backgroundColor: index === activeSlide ? '#818cf8' : '#475569',
                opacity: index === activeSlide ? 1 : 0.5,
              }}
              transition={{ duration: 0.3 }}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="px-6 pt-4 pb-10 space-y-4 pb-[calc(env(safe-area-inset-bottom)+2.5rem)]">
        {/* Primary CTA */}
        <Link
          to="/signup"
          className="w-full h-16 bg-purple-600 rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-500/40 active:scale-[0.98] transition-transform"
        >
          <Sparkles className="w-5 h-5 text-white" />
          <span className="text-white font-bold text-lg">Summon ContentSage</span>
          <ArrowRight className="w-5 h-5 text-white" />
        </Link>

        {/* Secondary CTA */}
        <button
          className="w-full h-14 rounded-2xl flex items-center justify-center gap-2 active:bg-slate-800/50 transition-colors"
        >
          <Play className="w-5 h-5 text-slate-400" />
          <span className="text-slate-300 font-semibold text-base">Watch 2-Min Demo</span>
        </button>

        {/* Trust Text */}
        <p className="text-slate-600 text-xs text-center">
          No credit card required &bull; 2 free articles included
        </p>
      </div>
    </div>
  );
};

export default MobileWelcomeScreen;
